const INTERNAL_SKILL_CODE = /^(?:[A-Z]{2,}(?:-[A-Z0-9]+)+|[A-Z]{2,}\d+(?:-\d+)+)$/;

export function formatSkill(value, fallback = 'Current skill') {
  const text = String(value || '').trim();
  if (!text) return fallback;
  if (INTERNAL_SKILL_CODE.test(text)) return fallback;
  // Already-human text (contains whitespace) passes through untouched —
  // title-casing prose mangles copy ("Chosen To Revisit A Weak Area...").
  // Only machine tokens (snake_case skill/family keys) get transformed.
  if (/\s/.test(text)) return text;
  return text
    .replace(/_+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatDateTime(value, options = {}) {
  if (!value) return 'Not available';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return new Intl.DateTimeFormat(options.locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...options,
  }).format(date);
}

export function formatMarks(value, precision = 2) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Not available';
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: precision,
  }).format(number);
}

export function formatSessionLabel({ mode, title } = {}) {
  const safeTitle = formatSkill(title, '');
  if (safeTitle) return safeTitle;
  return String(mode || '').toUpperCase() === 'EXAM' ? 'Exam paper' : 'Practice session';
}

export function formatRole(value) {
  const roles = { ADMIN: 'Admin', PARENT: 'Parent', STUDENT: 'Student', TUTOR: 'Tutor' };
  return roles[String(value || '').toUpperCase()] || 'Account';
}

// Session lifecycle vocabulary (Ryan's decision):
//   ACTIVE    — open and resumable
//   COMPLETED — a fixed session that reached its planned question count
//   ABANDONED — explicitly ended before completion
// An ABANDONED FIXED session stopped short of a known target, so it reads
// "Ended early"; an ENDLESS session has no target to fall short of, so it
// simply "Ended".
export function isEndlessQuestionCountMode(questionCountMode) {
  return String(questionCountMode || '').trim().toLowerCase() === 'endless';
}

export function isFixedQuestionCountMode(questionCountMode) {
  return String(questionCountMode || '').trim().toLowerCase() === 'fixed';
}

function abandonedLabel(questionCountMode) {
  // Fall back to the plain "Ended" when the payload does not carry
  // question_count_mode (the student dashboard's recent-session rows do not):
  // "Ended" is true of every abandoned session, whereas "Ended early" asserts a
  // planned count that may not exist.
  return isFixedQuestionCountMode(questionCountMode) ? 'Ended early' : 'Ended';
}

/**
 * Status pill copy for a session row (dashboard lists).
 * Accepts a raw session payload so question_count_mode is read from the source.
 */
export function formatSessionStatus(session) {
  const status = String(session?.status || '').trim().toUpperCase();
  if (status === 'COMPLETED') return 'Completed';
  if (status === 'ABANDONED') return abandonedLabel(session?.question_count_mode);
  return 'Active';
}

export function formatState(value, audience = 'general', options = {}) {
  const state = String(value || '').trim().toUpperCase();
  if (state === 'ABANDONED') {
    return abandonedLabel(options.questionCountMode);
  }
  const studentStates = {
    DRAFT: 'Not submitted yet',
    NOT_STARTED: 'Not started',
    PENDING: 'Not started',
    SUBMITTED: 'Submitted — marking in progress',
    MARKED: 'Marked',
    ENDED: 'Ended',
    COMPLETED: 'Completed',
  };
  const generalStates = { ...studentStates, ACTIVE: 'In progress', IN_PROGRESS: 'In progress' };
  return (audience === 'student' ? studentStates : generalStates)[state]
    || formatSkill(state.toLowerCase(), 'Not available');
}

export function formatBoolean(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Not known yet';
}
